import dayjs from "dayjs";
import { z } from "zod";
import { LANGUAGE_KEY_IN_LOCAL_STORAGE, MAX_PERCENTAGE_DECIMAL_PLACES } from "./helpers/constants";
import useLocalStorage from "./hooks/useLocalStorage";

export const removeEmptyFields = (obj) => {
    if (typeof obj !== "object" || obj === null) {
        return obj;
    }

    return Object.fromEntries(
        Object.entries(obj)
            .map(([key, value]) => [key, removeEmptyFields(value)]) // Recursively clean nested objects
            .filter(
                ([_, value]) =>
                    value !== null &&
                    value !== undefined &&
                    value !== "" &&
                    (typeof value !== "object" ||
                        (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0)),
            ),
    );
};

export function conditionalObject(obj, condition) {
    if (condition()) {
        return obj;
    }
    return {};
}

export function paginatedResponse(schema) {
    return z.object({
        count: z.number(),
        next: z.string().nullable(),
        previous: z.string().nullable(),
        results: z.array(schema),
    });
}

/**
 * Generate an Outlook calendar link from task form data
 *
 * @param data Task form data
 * @param options Additional options for the calendar event
 * @returns URL string that opens Outlook calendar composition with pre-filled data
 */
export function generateOutlookCalendarLink(data, options) {
    const { getTranslation } = options;

    const startDateTime = !options.isAllDay ? `${data.date}T${data.startTime}:00` : `${data.date}`;

    const endDateTime = !options.isAllDay
        ? `${data.date}T${data.stopTime}:00`
        : dayjs(data.date).add(1, "day").format("YYYY-MM-DD");

    const subjectText = options.customer
        ? `${data.name} - ${options.customer.name || ""} ${options.customer.surname || ""} `
        : data.name;

    let bodyText = `${data.note}  ----- `;

    bodyText += `${getTranslation("tasks.addTaskDialog.priority")}: ${getTranslation(`tasks.priorities.${data.priority}`)} ----- `;

    bodyText += `${getTranslation("tasks.addTaskDialog.subject")}: ${options.subjects?.find((s) => s.value === data.subject)?.label || ""}  ----- `;
    if (data.subsubject) {
        bodyText += `${getTranslation("tasks.addTaskDialog.subsubject")}: ${options.subsubjects?.find((s) => s.value === data.subsubject)?.label || ""}  ----- `;
    }

    bodyText += `${getTranslation("tasks.addTaskDialog.customer")}: ${options.customer?.name || ""} ${
        options.customer?.surname || ""
    }`;

    const params = new URLSearchParams({
        subject: subjectText,
        startdt: startDateTime,
        enddt: endDateTime,
        allday: options.isAllDay ? "true" : "false",
        body: bodyText,
    });

    const url = `https://outlook.office.com/calendar/action/compose?${params.toString()}`;

    console.log("Generated Outlook Calendar URL:", url);
    return url;
}

export const datetimeWithOffset = z.string().datetime({ offset: true });

/**
 * Calculate prices for a given net price per square meter
 */
export function calculatePrices(input) {
    const netPerSqm =
        typeof input.netPerSqm === "string" ? Number.parseFloat(input.netPerSqm) : input.netPerSqm;
    const vatPercent =
        typeof input.vatPercent === "string"
            ? Number.parseFloat(input.vatPercent)
            : input.vatPercent;

    const net = netPerSqm * input.footage;
    const grossPerSqm = netPerSqm * (1 + vatPercent / 100);
    const gross = net * (1 + vatPercent / 100);

    return {
        net,
        net_per_sqm: netPerSqm,
        gross,
        gross_per_sqm: grossPerSqm,
    };
}

/**
 * Get the footage for a product
 */

function getEffectiveFootage(product) {
    return product.footage_postconstruction ?? product.footage ?? 0;
}

/**
 * Get the effective footage for a product, prioritizing post-construction footage
 */
function getFootage(product) {
    return product.footage ?? 0;
}

/**
 * Calculate all pricing tiers for a product
 */
export function calculateProductPricing(pricing, footage) {
    const commonInput = {
        footage,
        vatPercent: pricing.vat_proc,
    };

    return {
        listed: calculatePrices({
            ...commonInput,
            netPerSqm: pricing.listed_net_per_m2,
        }),
        minSale: calculatePrices({
            ...commonInput,
            netPerSqm: pricing.min_net_per_m2,
        }),
        minBase: calculatePrices({
            ...commonInput,
            netPerSqm: pricing.base_net_per_m2,
        }),
    };
}

export function getCalculatedPricingForProduct(product, useEffectiveFootage=false) {
    return calculateProductPricing(product.pricing, useEffectiveFootage ? getEffectiveFootage(product) : getFootage(product));
}

export function getCalculatedPricingForProductDiscounted(product, discountPercent, useEffectiveFootage=false) {
    let discount;
    if (typeof discountPercent === "string") {
        discount = Number.parseFloat(discountPercent || "0");
    } else {
        discount = discountPercent || 0;
    }
    const baseListed = Number.parseFloat(product.pricing.listed_net_per_m2 || "0");
    const discountedNetPerSqm = baseListed * (1 - discount / 100);

    return calculateProductPricing(
        {
            listed_net_per_m2: discountedNetPerSqm,
            min_net_per_m2: product.pricing.min_net_per_m2,
            base_net_per_m2: product.pricing.base_net_per_m2,
            vat_proc: product.pricing.vat_proc,
        },
        useEffectiveFootage ? getEffectiveFootage(product) : getFootage(product),
    );
}

export function getDiscountedPricesForProduct(product, discountPercent, useEffectiveFootage=false) {
    const discount =
        typeof discountPercent === "string"
            ? Number.parseFloat(discountPercent || "0")
            : discountPercent || 0;

    const vatPercent =
        product.pricing?
        (typeof product.pricing.vat_proc === "string"
            ? Number.parseFloat(product.pricing.vat_proc || "0")
            : product.pricing.vat_proc || 0)
            : 0
    const baseListed = Number.parseFloat(product?.pricing?.listed_net_per_m2 || "0");

    const discountedNetPerSqm = baseListed * (1 - discount / 100);
    return calculatePrices({
        netPerSqm: discountedNetPerSqm,
        footage: useEffectiveFootage ? getEffectiveFootage(product) : getFootage(product),
        vatPercent,
    });
}

export const useLanguage = () => {
    const [language, _] = useLocalStorage(LANGUAGE_KEY_IN_LOCAL_STORAGE, "pl");
    return language;
};

export function getLocalizedProperty(obj, basePropertyName, language = "pl") {
    if (!obj || typeof obj !== "object") {
        return "";
    }

    const patterns = [
        {
            polish: basePropertyName,
            english: `${basePropertyName}_eng`,
        },
    ];

    for (const pattern of patterns) {
        const polishValue = obj[pattern.polish];
        const englishValue = obj[pattern.english];

        if (
            polishValue &&
            typeof polishValue === "string" &&
            polishValue.trim() !== "" &&
            language === "pl"
        ) {
            return polishValue;
        }

        if (
            englishValue &&
            typeof englishValue === "string" &&
            englishValue.trim() !== "" &&
            language === "en"
        ) {
            return englishValue;
        }
    }

    return "";
}

export function useLocalizedProperty() {
    const language = useLanguage();

    return (obj, basePropertyName) => getLocalizedProperty(obj, basePropertyName, language);
}

export function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

export const setsEqual = (xs, ys) => {
    if (xs.size !== ys.size) {
        return false;
    }
    return Array.from(xs).every((x) => ys.has(x));
};

/**
 * No operation function
 * Can be used as a default placeholder for callbacks
 */
export const nop = () => {};

export function getFractionStringFromNumber(num) {
  const numStr = num.toString();
  if (!numStr.includes('.')) return ""; // Brak części ułamkowej
  
  return numStr.split('.')[1];
}

export function formatDecimalNumber(value, decimalPlaces = 2) {
    if (value === null || value === undefined || value === "") {
        return "";
    }

    const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

    if (Number.isNaN(numValue)) {
        return "";
    }

    const fractionPart = getFractionStringFromNumber(numValue) 
    if (fractionPart.length > decimalPlaces) {
        return numValue.toFixed(decimalPlaces)
    }

    return numValue;
}

export function normalizePercentageForSubmission(value) {
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    const numValue = typeof value === "string" ? Number.parseFloat(value) : value;

    if (Number.isNaN(numValue)) {
        return 0;
    }

    return Number.parseFloat(numValue.toFixed(MAX_PERCENTAGE_DECIMAL_PLACES));
}
